import { isValidPhoneNumber } from 'react-phone-number-input';

export type FieldValidator<T = string> = (value: T) => string | null;

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const DEFAULT_EMAIL_ERROR = 'Ошибка в e-mail';
const DEFAULT_PHONE_ERROR = 'Ошибка в номере';
const DEFAULT_NAME_LENGTH_ERROR = 'Длина имени должна быть от 3 до 30 символов';

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 40;

export const EMAIL_MAX_LENGTH = 40;
export const PHONE_MAX_LENGTH = 16;

export const HEX_COLOR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

/**
 * Универсальный валидатор длины текста.
 * Пропускает пустые значения (проверка required — отдельная ответственность).
 * Если не передан ни min, ни max — валидация всегда проходит.
 */
export function createTextLengthValidator(
  { min, max }: { min?: number | null; max?: number | null },
  errorMessage?: string
): FieldValidator<string> {
  const hasMin = !!min;
  const hasMax = !!max;

  if (!hasMin && !hasMax) {
    return () => null;
  }

  const message = errorMessage ?? buildLengthErrorMessage(min, max);

  return (value: string): string | null => {
    if (value == null || String(value).trim() === '') {
      return null;
    }

    const len = String(value).trim().length;

    if (hasMin && len < min) {
      return message;
    }

    if (hasMax && len > max) {
      return message;
    }

    return null;
  };
}

function buildLengthErrorMessage(
  min?: number | null,
  max?: number | null
): string | null {
  if (!!min && !!max) {
    return `Длина должна быть от ${min} до ${max} символов`;
  }

  if (min) {
    return `Длина должна быть от ${min} символов`;
  }

  if (max) {
    return `Длина должна быть до ${max} символов`;
  }

  return null;
}

export function createEmailValidator(
  errorMessage: string = DEFAULT_EMAIL_ERROR
): FieldValidator<string> {
  return (value: string): string | null => {
    if (value == null || String(value).trim() === '') {
      return null;
    }
    return EMAIL_REGEX.test(String(value).trim()) ? null : errorMessage;
  };
}

export function createPhoneValidator(
  errorMessage: string = DEFAULT_PHONE_ERROR
): FieldValidator<string> {
  return (value: string): string | null => {
    if (value == null || String(value).trim() === '') {
      return null;
    }
    return isValidPhoneNumber(value.trim()) ? null : errorMessage;
  };
}

export function createNameLengthValidator(
  minLength: number = NAME_MIN_LENGTH,
  maxLength: number = NAME_MAX_LENGTH,
  errorMessage: string = DEFAULT_NAME_LENGTH_ERROR
): FieldValidator<string> {
  return createTextLengthValidator(
    { min: minLength, max: maxLength },
    errorMessage
  );
}

export const validateHexColor = (color: string): boolean => {
  return HEX_COLOR_REGEX.test(color.trim());
};

export const validateEmail: FieldValidator<string> = createEmailValidator();
export const validatePhone: FieldValidator<string> = createPhoneValidator();
export const validateName: FieldValidator<string> = createNameLengthValidator();
